import React from 'react';
import { useLocation, useParams, Link, Redirect } from 'react-router-dom';
import { Font, StyleSheet, Text, View, PDFDownloadLink } from '@react-pdf/renderer';
import XPDF from 'src/components/pdf/XPDF';
import PDFTable from 'src/pages/cart/components/pdf/PDFTable';
import { Content } from 'src/components/light/Content';
import { SectionBlock } from 'src/components/light/SectionBlock';
import moment from 'moment';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const source = require('src/webfonts/khmer-regular.ttf');
Font.register({
  family: 'Khmer',
  src: source,
});

const styles = StyleSheet.create({
  infoContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    marginBottom: 10,
    fontFamily: 'Khmer',
    fontSize: '10pt',
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function InvoiceDocument({ invoice }: { invoice: any }) {
  if (!invoice) return null;

  const total_usd = Number(invoice.amount) || 0;
  const vat_included = total_usd * (10 / 110);

  return (
    <XPDF>
      <View style={{ padding: 20, fontFamily: 'Khmer' }}>
        <XPDF.InvoiceHeader />

        {/* Info */}
        <View style={styles.infoContainer}>
          <View>
            <Text style={{ fontWeight: 'bold', fontSize: '12pt', marginBottom: 5, fontFamily: 'Khmer' }}>អតិថិជន</Text>
            <Text style={{ fontFamily: 'Khmer' }}>+855 {invoice.customer_phone || ''}</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text>{moment.utc(invoice.invoice_date).format('DD/MM/YYYY')}</Text>
            <Text>{invoice.invoice_no}</Text>
          </View>
        </View>

        {/* Table */}
        <View style={{ marginTop: 10 }}>
          <PDFTable border>
            <PDFTable.thead>
              <PDFTable.row>
                <PDFTable.th text="Description" align="left" width={360} />
                <PDFTable.th text="Qty." width={40} align="right" />
                <PDFTable.th text="Unit Price" width={75} align="right" />
                <PDFTable.th text="Amount" width={75} align="right" />
              </PDFTable.row>
            </PDFTable.thead>
            <PDFTable.tbody>
              <PDFTable.row>
                <PDFTable.td text="សេវាដឹកជញ្ជូន" align="left" width={360} />
                <PDFTable.td text="1" width={40} align="right" />
                <PDFTable.td
                  text={`$${total_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                  width={75}
                  align="right"
                />
                <PDFTable.td
                  text={`$${total_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                  width={75}
                  align="right"
                />
              </PDFTable.row>

              {/* Totals */}
              <PDFTable.row>
                <PDFTable.td text="Total (USD)" align="left" width={360} />
                <PDFTable.td text="" width={40} />
                <PDFTable.td text="" width={75} />
                <PDFTable.td
                  text={`$${total_usd.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                  width={75}
                  align="right"
                />
              </PDFTable.row>
              <PDFTable.row>
                <PDFTable.td text="VAT Included (10%)" align="left" width={360} />
                <PDFTable.td text="" width={40} />
                <PDFTable.td text="" width={75} />
                <PDFTable.td
                  text={`$${vat_included.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                  width={75}
                  align="right"
                />
              </PDFTable.row>
            </PDFTable.tbody>
          </PDFTable>
        </View>
      </View>
    </XPDF>
  );
}

export function FblDeliveryInvoiceDetailScreen() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const location = useLocation<{ invoice: any }>();
  const { invoiceNo } = useParams<{ invoiceNo: string }>();
  const [isFontLoaded, setIsFontLoaded] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setIsFontLoaded(true), 500);
    return () => clearTimeout(timer);
  }, []);

  let invoice = location.state?.invoice;

  if (!invoice) {
    const saved = localStorage.getItem(`fbl_preview_invoice_${invoiceNo}`);
    if (saved) {
      try {
        invoice = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse invoice from local storage', e);
      }
    }
  }

  if (!invoice) {
    return <Redirect to="/invoices/fbl-delivery" />;
  }

  return (
    <Content>
      <SectionBlock title={`Invoice Preview: ${invoice.invoice_no}`}>
        <div className="mb-3">
          <Link className="btn btn-secondary btn-sm" to="/invoices/fbl-delivery">
            &larr; Back to List
          </Link>
        </div>
        <div>
          <div className="mb-3 text-right">
            <PDFDownloadLink
              className="btn btn-primary"
              document={<InvoiceDocument invoice={invoice} />}
              fileName={`FBLDelivery_Invoice_${invoice.invoice_no}.pdf`}
            >
              {({ loading }) => (loading ? 'Preparing PDF...' : 'Download PDF')}
            </PDFDownloadLink>
          </div>

          <div style={{ height: '70vh', width: '100%', border: '1px solid #ccc' }}>
            {isFontLoaded ? (
              <XPDF.Preview>
                <InvoiceDocument invoice={invoice} />
              </XPDF.Preview>
            ) : (
              <div style={{ padding: 20, textAlign: 'center' }}>Loading preview...</div>
            )}
          </div>
        </div>
      </SectionBlock>
    </Content>
  );
}
